// Story 8.1 (ADO-1092) — initialise-basket (depot-selection) client (FR21).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport. On
// depot-confirm the SPA POSTs the chosen depot; the server opens (or returns the
// existing) DRAFT basket for the caller's (tenant, depot, value-centre) and
// replies with a summary. Distinct from `basket.client.ts` (Story 8.2's
// `/basket/items` line lifecycle) — this is `POST /api/v1/basket`.
//
// The wire shape is a LOCK-STEP COPY of `BasketSummaryDto` exported by the
// order-capture module — keep the two in sync. 8.1 sends only `depotId`; the
// server defaults `value_centre_id = depot_code` (D-6), and VC selection is 8.5.

import { cartTargetBody, type CartTarget } from './cart-target';
import { API_PREFIX, apiFetch } from './shoplink-api';

export interface BasketSectionSummary {
  readonly sectionId: string;
  readonly sectionName: string;
  readonly status: string;
}

/** The caller's active basket summary (lock-step with `BasketSummaryDto`). */
export interface BasketSummary {
  readonly cartId: string;
  readonly depotId: string;
  readonly valueCentreId: string;
  readonly status: string;
  /** True when this request OPENED the basket; false when directed to an existing
   *  one for the same (tenant, depot, value-centre) — AC3. */
  readonly created: boolean;
  readonly sections: readonly BasketSectionSummary[];
}

export const BASKET_INIT_PATH = `${API_PREFIX}/basket`;

function isSection(value: unknown): value is BasketSectionSummary {
  const s = value as BasketSectionSummary | null;
  return (
    typeof s === 'object' &&
    s !== null &&
    typeof s.sectionId === 'string' &&
    typeof s.sectionName === 'string' &&
    typeof s.status === 'string'
  );
}

/**
 * Parse + validate the basket-summary body. A 2xx whose body is not the expected
 * shape is a contract violation — surface a clear error rather than letting a
 * downstream `undefined.cartId` throw a cryptic TypeError.
 */
function parseBasketSummary(body: unknown): BasketSummary {
  const b = body as Partial<BasketSummary> | null;
  if (
    b === null ||
    typeof b.cartId !== 'string' ||
    typeof b.depotId !== 'string' ||
    typeof b.valueCentreId !== 'string' ||
    typeof b.status !== 'string' ||
    typeof b.created !== 'boolean' ||
    !Array.isArray(b.sections) ||
    !b.sections.every(isSection)
  ) {
    throw new Error('Basket summary response was not in the expected shape');
  }
  return {
    cartId: b.cartId,
    depotId: b.depotId,
    valueCentreId: b.valueCentreId,
    status: b.status,
    created: b.created,
    sections: b.sections,
  };
}

/**
 * Open (get-or-open) the DRAFT basket for `cart` on depot-confirm (AC2), the depot plus
 * the Shopper's Trading Account (Story 8.3, ADO-2012, D-1; staff send no account).
 * Idempotent server-side (get-or-open); returns the summary + `created` so the caller can
 * route/toast (AC3).
 */
export async function openBasket(cart: CartTarget): Promise<BasketSummary> {
  const res = await apiFetch(BASKET_INIT_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cartTargetBody(cart)),
  });
  if (!res.ok) throw new Error(`Failed to open basket (${res.status})`);
  return parseBasketSummary(await res.json());
}
